import { Component } from 'preact';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { RADIUS, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, GOLD } from '../../lib/medievalTheme';
import { getClass } from '../../utils/arena/classes';
import {
  ALCHEMIST_POTIONS, HUMOURS, POTION_RECIPES, POTION_POTENCIES,
  potionArt, potionArtMask, potionPotency, potionEffect, potionStock, drinkPotion,
} from '../../utils/arena/alchemist';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { Tooltip } from '../ui/tooltip';
import ForgedButton from '../ui/ForgedButton';
import ElementIcon from '../deckStats/ElementIcon';
import PotionPaint from './PotionPaint';
import { POTION_LIQUID } from './potionLiquid';
import { t } from '../../localization/t';

const CLS = getClass('alchemist');
const ACCENT = CLS.accent;
const SPRING = { type: 'spring', duration: 0.4, bounce: 0 };
// Active potions at once. Mirrors ACTIVE_SLOTS in the server's alchemist.js.
export const ACTIVE_SLOTS = 4;

const potionOf = (key) => ALCHEMIST_POTIONS.find((p) => p.key === key);
const potionName = (key, potency) => t('{potency} Potion of {name}', { potency: t(potionPotency(potency)?.title || 'Ordinary'), name: t(potionOf(key)?.title || key) });
const whenLine = (key) => (POTION_RECIPES[key]?.kind === 'match' ? t('Works on the next match.') : t('Works on the next booster.'));

// Every bottle on the shelf, one entry per potion and potency held.
export function bottles(alchemist) {
  const out = [];
  for (const p of ALCHEMIST_POTIONS) {
    for (const tier of POTION_POTENCIES) {
      const n = potionStock(alchemist, p.key, tier.key);
      if (n > 0) out.push({ key: p.key, potency: tier.key, stock: n });
    }
  }
  return out;
}

// Every candidate shares one grid cell, so the longest copy reserves its
// height and the plate never moves when another bottle is chosen.
function ReservedText({ candidates, children, className = '', style = {}, testId }) {
  return (
    <div className={className} style={{ display: 'grid', width: '100%', ...style }}>
      {candidates.map((text, i) => <div key={i} aria-hidden style={{ gridArea: '1 / 1', visibility: 'hidden', pointerEvents: 'none' }}>{text}</div>)}
      <div style={{ gridArea: '1 / 1' }} data-testid={testId}>{children}</div>
    </div>
  );
}
const ALL_NAMES = ALCHEMIST_POTIONS.flatMap((p) => POTION_POTENCIES.map((tier) => potionName(p.key, tier.key)));
const ALL_EFFECTS = ALCHEMIST_POTIONS.flatMap((p) => POTION_POTENCIES.map((tier) => potionEffect(p.key, tier.key)));
const ALL_WHEN = [t('Works on the next match.'), t('Works on the next booster.')];

// An active slot: the tree's node ornament lies underneath, a round dark
// socket sits on it, and the bottle stands in the socket. The ornament
// shows as a rim around the socket, lit in the liquid's colour when the slot
// holds a bottle.
const SLOT = 6.25;
const SOCKET = 4.9;
function Socket({ potionKey, children, testId }) {
  const color = POTION_LIQUID[potionKey]?.color;
  const lit = Boolean(color);
  return (
    <div className="relative flex items-center justify-center shrink-0" style={{ width: `${SLOT}cqw`, height: `${SLOT}cqw`, '--potion-glow': color }} data-testid={testId} data-potion-socket={potionKey || 'empty'}>
      <span
        aria-hidden
        className={`vh-skill-node-ornament${lit ? ' vh-potion-socket-light' : ''}`}
        style={{
          '--vh-skill-node-ornament-svg': `url("${CLS.ornament}")`, '--vh-skill-node-accent': color || ACCENT,
          // The ornament's own box is wider than the slot, so nothing is
          // cropped at the slot's edge; the art fills it at its natural size.
          inset: '-17%', WebkitMaskSize: '100% 100%', maskSize: '100% 100%',
          opacity: lit ? 1 : 0.42,
          filter: lit ? `drop-shadow(0 0 0.09375cqw ${color}) drop-shadow(0 0 0.375cqw ${color})` : 'saturate(0.7)',
        }}
      />
      <div
        className="relative flex items-center justify-center rounded-full"
        style={{
          width: `${SOCKET}cqw`, height: `${SOCKET}cqw`,
          background: 'radial-gradient(circle at 50% 38%, #1a1420 0%, #0e0c10 68%, #08070a 100%)',
          boxShadow: [
            `0 0 0 0.125cqw #08070a`,
            '0 0 0 0.2cqw #2a2331',
            'inset 0 1px 0 rgba(255,255,255,0.07)',
            'inset 0 0.3cqw 0.8cqw rgba(0,0,0,0.75)',
          ].join(', '),
        }}
      >
        {lit ? <span aria-hidden className="absolute inset-0 rounded-full pointer-events-none vh-potion-socket-light" style={{
          background: `radial-gradient(ellipse at 50% 70%, ${color}38, ${color}10 50%, transparent 75%)`,
          boxShadow: `0 0 0 0.075cqw ${color}88, 0 0 1cqw ${color}38, inset 0 0 0.8cqw ${color}55`,
        }} /> : null}
        <span className="relative flex items-center justify-center">{children}</span>
      </div>
    </div>
  );
}

function Plate({ potionKey, size }) {
  return (
    <img
      src={potionArt(potionKey)} alt="" draggable={false}
      style={{ width: `${size}cqw`, height: `${size}cqw`, objectFit: 'contain', maskImage: potionArtMask(potionKey), maskSize: '100% 100%' }}
    />
  );
}

// A shelf row, dressed exactly as the laboratory's potion rows: the same
// classes, the same painted wash on hover, the same painted selection
// drawn on click, the same ink variables.
function ShelfRow({ bottle, selected, drawSelection, onSelect, onHover }) {
  const paintSeed = potionName(bottle.key, bottle.potency);
  return (
    <button
      type="button"
      data-testid={`shelf-${bottle.key}-${bottle.potency}`}
      data-selected={selected}
      onClick={() => onSelect(`${bottle.key}:${bottle.potency}`)}
      onMouseEnter={onHover}
      aria-pressed={selected}
      className={`vh-lab-row relative flex items-center shrink-0 w-full text-left cursor-pointer${selected ? ' vh-lab-row-selected' : ''}`}
      style={{
        gap: '0.85cqw', padding: '0.35cqw 0.75cqw',
        '--row-ink': selected ? '#23201b' : '#e9decb',
        '--row-muted': selected ? '#51483c' : '#978e81',
        '--row-cost': selected ? '#23201b' : '#e9decb',
      }}
    >
      <PotionPaint seed={paintSeed} className="vh-lab-row-wash" />
      {selected ? <PotionPaint key={bottle.potency} seed={paintSeed} className="vh-lab-row-selection" draw={drawSelection} /> : null}
      <span className="relative shrink-0 flex items-center justify-center" style={{ width: '4.5cqw', height: '4.5cqw' }}>
        <img src={potionArt(bottle.key)} alt="" draggable={false} className="relative"
          style={{ width: '100%', height: '100%', objectFit: 'contain', maskImage: potionArtMask(bottle.key), maskSize: '100% 100%' }} />
      </span>
      <span className="relative flex flex-col min-w-0 flex-1" style={{ gap: '0.125cqw' }}>
        <span className="vh-lab-row-name arena-heading relative inline-flex self-start max-w-full" style={{ padding: '0.15cqw 0.4cqw', fontSize: '1.1cqw', letterSpacing: '0.02em' }}>
          <span className="relative whitespace-nowrap" style={{ lineHeight: 1.3 }}>{potionName(bottle.key, bottle.potency)}</span>
        </span>
        <span className="truncate" style={{ color: 'var(--row-muted)', fontSize: '0.8cqw', paddingLeft: '0.4cqw' }}>{whenLine(bottle.key)}</span>
      </span>
      <span className="relative arena-heading tabular-nums shrink-0" style={{ color: 'var(--row-cost)', fontSize: '1.1cqw', minWidth: '2cqw', textAlign: 'right' }}>×{bottle.stock}</span>
    </button>
  );
}

function Heading({ children }) {
  return (
    <div className="flex items-center" style={{ gap: '0.75cqw' }}>
      <span className="arena-heading" style={{ color: ACCENT, fontSize: '1.0625cqw', letterSpacing: '0.04em' }}>{children}</span>
      <span aria-hidden className="flex-1" style={{ height: '1px', background: `linear-gradient(90deg, ${GOLD} 0.45), transparent)` }} />
    </div>
  );
}

// The Alchemist's body of the class plate: the four humours large, the
// active bottles in sockets, and the shelf with the chosen bottle and its
// Drink plate. Drinking answers with the blob; the server's profile push
// follows.
export default class AlchemistPlate extends Component {
  state = { selected: null, busy: false, alchemist: null, drawSelection: false };

  get alchemist() { return this.state.alchemist || this.props.profile?.alchemist || {}; }

  componentDidUpdate(prevProps) {
    if (prevProps.profile?.alchemist !== this.props.profile?.alchemist && this.state.alchemist) this.setState({ alchemist: null });
  }

  get selectedId() {
    const list = bottles(this.alchemist);
    const found = list.find((b) => `${b.key}:${b.potency}` === this.state.selected);
    return found ? this.state.selected : list[0] ? `${list[0].key}:${list[0].potency}` : null;
  }

  select = (id) => {
    if (id === this.selectedId) return;
    playUI(UI.UNEQUIP);
    this.setState({ selected: id, drawSelection: true });
  };

  hover = () => playUI(UI.HOVER, { volume: 0.3 });

  drink = async (key, potency) => {
    if (this.state.busy) return;
    this.setState({ busy: true });
    try {
      const next = await drinkPotion(key, potency);
      playUI(UI.DRINK);
      this.setState({ busy: false, alchemist: next });
    } catch (err) {
      playUI(UI.ERROR);
      this.setState({ busy: false });
      toast.error(err?.status === 409 ? t('A Potion of {name} is already active.', { name: t(potionOf(key)?.title || key) }) : t('Could not drink that potion.'));
    }
  };

  render() {
    const alchemist = this.alchemist;
    const { busy } = this.state;
    const active = ALCHEMIST_POTIONS.filter((p) => alchemist.active?.[p.key]?.uses > 0);
    const shelf = bottles(alchemist);
    const selectedId = this.selectedId;
    const chosen = shelf.find((b) => `${b.key}:${b.potency}` === selectedId) || null;
    const chosenReadied = chosen ? alchemist.active?.[chosen.key]?.uses > 0 : false;
    const slotsFull = active.length >= ACTIVE_SLOTS;

    return (
      <div className="relative flex flex-col" style={{ gap: '1.125cqw' }} data-testid="alchemist-plate">
        {/* The humours. */}
        <div className="flex items-center justify-center" style={{ gap: '3.5cqw' }} data-testid="plate-humours">
          {HUMOURS.map((h) => (
            <Tooltip key={h.key} side="bottom" maxWidth="14cqw" content={<div className="font-normal" style={{ lineHeight: 1.5 }}>{t(h.note)}</div>}>
              <span className="flex items-center cursor-help" style={{ gap: '0.625cqw' }}>
                <ElementIcon element={h.element} size={34} />
                <span className="flex flex-col" style={{ gap: '0.125cqw' }}>
                  <span className="arena-heading tabular-nums" style={{ color: TEXT_PRIMARY, fontSize: '1.625cqw', lineHeight: 1 }} data-testid={`plate-humour-${h.key}`}>{alchemist[h.key] || 0}</span>
                  <span style={{ color: TEXT_BODY, fontSize: '0.8125cqw' }}>{t(h.label)}</span>
                </span>
              </span>
            </Tooltip>
          ))}
        </div>

        {/* Active: what the next match or booster will receive. */}
        <div className="flex flex-col" style={{ gap: '0.625cqw' }}>
          <Heading>{t('Active')}</Heading>
          <div className="flex justify-between" style={{ padding: '0 2cqw' }} data-testid="readied-grid">
              {Array.from({ length: ACTIVE_SLOTS }, (_, i) => active[i] || null).map((p, i) => {
                if (!p) {
                  return (
                    <div key={`slot-${i}`} className="flex flex-col items-center" style={{ width: '9cqw', gap: '0.5cqw' }}>
                      <Socket testId={`readied-slot-${i}`}>
                        <span aria-hidden className="rounded-full" style={{ width: '0.5cqw', height: '0.5cqw', background: '#2a2331' }} />
                      </Socket>
                      <span className="text-center" style={{ color: TEXT_MUTED, fontSize: '0.75cqw', lineHeight: 1.3, minHeight: '2cqw' }}>{t('Empty')}</span>
                    </div>
                  );
                }
                const potency = alchemist.active[p.key].potency || 'ordinary';
                return (
                  <div key={p.key} className="flex flex-col items-center" style={{ width: '9cqw', gap: '0.5cqw' }}>
                  <Tooltip side="bottom" maxWidth="20cqw" content={(
                    <>
                      <div className="font-bold mb-1" style={{ color: ACCENT, fontSize: '0.6875cqw' }}>{potionName(p.key, potency)}</div>
                      <div className="font-normal" style={{ lineHeight: 1.5 }}>{potionEffect(p.key, potency)}</div>
                      <div className="font-normal mt-1" style={{ color: TEXT_MUTED }}>{whenLine(p.key)}</div>
                    </>
                  )}>
                    <motion.div className="cursor-help" whileHover={{ scale: 1.06 }} transition={SPRING} data-testid={`readied-${p.key}`}>
                      <Socket potionKey={p.key}>
                        <Plate potionKey={p.key} size={4} />
                      </Socket>
                    </motion.div>
                  </Tooltip>
                  <span className="arena-heading text-center" style={{ color: TEXT_PRIMARY, fontSize: '0.8125cqw', lineHeight: 1.3, minHeight: '2cqw' }}>{potionName(p.key, potency)}</span>
                  </div>
                );
              })}
          </div>
          <span style={{ color: TEXT_MUTED, fontSize: '0.8125cqw', minHeight: '1.2cqw' }}>
            {slotsFull ? t('Every slot is taken. They free as matches and boosters use them.') : active.length ? t('{n} of {total} slots active.', { n: active.length, total: ACTIVE_SLOTS }) : t('Nothing active. Drink a potion from the shelf.')}
          </span>
        </div>

        {/* The shelf: bottles on the left, the chosen one on the right. */}
        <div className="flex flex-col" style={{ gap: '0.625cqw' }}>
          <Heading>{t('Shelf')}</Heading>
          <div className="flex items-stretch" style={{ gap: '1.5cqw', height: '23cqw' }}>
              {/* Wide enough for the longest name on one line. */}
              <div className="flex flex-col overflow-y-auto overflow-x-hidden" style={{ width: '30cqw', gap: '0.2cqw', padding: '0.35cqw 0.75cqw' }} data-testid="shelf-list">
                {shelf.length ? null : <span style={{ color: TEXT_MUTED, fontSize: '0.875cqw', padding: '0.5cqw 0.75cqw' }}>{t('No potions on the shelf. Brew some in the laboratory.')}</span>}
                {shelf.map((b) => {
                  const id = `${b.key}:${b.potency}`;
                  return <ShelfRow key={id} bottle={b} selected={id === selectedId} drawSelection={this.state.drawSelection} onSelect={this.select} onHover={this.hover} />;
                })}
              </div>
              {/* The chosen bottle. Its column keeps the longest copy's height, bottle or no bottle. */}
              <div className="flex flex-col items-center flex-1 min-w-0 text-center" style={{ gap: '0.375cqw', padding: '0 0.5cqw', visibility: chosen ? 'visible' : 'hidden' }} data-testid="shelf-detail">
                <Plate potionKey={chosen?.key || 'lucidity'} size={7.5} />
                <ReservedText className="arena-heading" candidates={ALL_NAMES} style={{ color: ACCENT, fontSize: '1.25cqw', lineHeight: 1.3, textShadow: `0 0 0.75cqw ${ACCENT}44` }}>
                  {chosen ? potionName(chosen.key, chosen.potency) : null}
                </ReservedText>
                <ReservedText candidates={ALL_EFFECTS} style={{ color: TEXT_PRIMARY, fontSize: '0.9cqw', lineHeight: 1.4 }}>
                  {chosen ? potionEffect(chosen.key, chosen.potency) : null}
                </ReservedText>
                <ReservedText candidates={ALL_WHEN} style={{ color: TEXT_BODY, fontSize: '0.8125cqw' }}>
                  {chosen ? whenLine(chosen.key) : null}
                </ReservedText>
                <ForgedButton style={{ marginTop: '0.5cqw' }} tone="gold" disabled={busy || chosenReadied || slotsFull || !chosen} onClick={() => chosen && this.drink(chosen.key, chosen.potency)} radius={RADIUS.xl}
                  className="arena-heading w-[9cqw] px-5 py-3 text-[1.25cqw] tracking-wide whitespace-nowrap text-center">
                  {chosenReadied ? t('Active') : slotsFull ? t('Slots full') : t('Drink')}
                </ForgedButton>
              </div>
          </div>
        </div>
      </div>
    );
  }
}
